import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SubscriptionBanner, { useSubscriptionStatus } from '@/components/SubscriptionBanner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, FileText, TrendingUp, Plus, Calendar, CheckCircle, ArrowRight, Sparkles, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function AnimatedNumber({ value, duration = 1 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  useEffect(() => {
    if (isInView && value > 0) {
      let start = 0;
      const end = parseInt(value);
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) { setDisplayValue(end); clearInterval(timer); }
        else setDisplayValue(Math.floor(start));
      }, 1000 / 60);
      return () => clearInterval(timer);
    } else if (value === 0) setDisplayValue(0);
  }, [isInView, value, duration]);
  return <span ref={ref}>{displayValue}</span>;
}

function AnimatedProgress({ value, gradient, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  return (
    <div ref={ref} className="mt-3 w-full bg-gray-200 dark:bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
      <motion.div
        className={`h-1.5 rounded-full ${gradient}`}
        initial={{ width: 0 }}
        animate={isInView ? { width: `${Math.min(100, value || 0)}%` } : { width: 0 }}
        transition={{ duration: 1, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      />
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div data-testid="dashboard-skeleton" className="max-w-7xl mx-auto animate-pulse">
      <div className="h-10 w-72 bg-gray-800 rounded-lg mb-2" />
      <div className="h-5 w-56 bg-gray-800/60 rounded mb-8" />
      <div className="h-16 bg-gray-800/40 rounded-xl mb-8" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-36 bg-gray-800/40 rounded-xl" />
        ))}
      </div>
      <div className="h-32 bg-gray-800/40 rounded-xl mb-8" />
      <div className="h-48 bg-gray-800/40 rounded-xl" />
    </div>
  );
}

const STAT_CONFIGS = [
  { key: 'clients', label: 'Client Capacity', icon: Users, gradient: 'from-blue-500 to-cyan-400', progressGradient: 'bg-gradient-to-r from-blue-500 to-cyan-400', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400' },
  { key: 'plans', label: 'Plan Capacity', icon: FileText, gradient: 'from-emerald-500 to-teal-400', progressGradient: 'bg-gradient-to-r from-emerald-500 to-teal-400', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
  { key: 'total', label: 'Total Plans Created', icon: TrendingUp, gradient: 'from-purple-500 to-violet-400', progressGradient: '', iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400' },
  { key: 'plan_tier', label: 'Current Plan', icon: CheckCircle, gradient: 'from-orange-500 to-amber-400', progressGradient: '', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-400' },
];

export default function AdvisorDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [recentPlans, setRecentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const { status: subscriptionStatus } = useSubscriptionStatus();
  const isMobile = useIsMobile();

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const usageResp = await axios.get(`${API}/advisor/usage?t=${Date.now()}`, {
        headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      setUsage(usageResp.data);
      if (usageResp.data.subscription_status === 'expired') {
        const hasSeenExpiredModal = localStorage.getItem('hasSeenExpiredModal');
        if (!hasSeenExpiredModal) {
          localStorage.setItem('hasSeenExpiredModal', 'true');
          navigate('/advisor/pricing?expired=true&firstTime=true');
          return;
        }
      }
      const plansResp = await axios.get(`${API}/advisor/professional-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentPlans((plansResp.data.plans || []).slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <SkeletonLoader />
      </div>
    );
  }

  const getStatValue = (key) => {
    if (key === 'clients') return usage?.clients_count || 0;
    if (key === 'plans') return usage?.plans_this_month || 0;
    if (key === 'total') return usage?.total_plans || 0;
    return null;
  };

  const getStatLimit = (key) => {
    if (key === 'clients') return usage?.subscription_status === 'expired' ? '0' : (usage?.client_limit || 0);
    if (key === 'plans') return usage?.subscription_status === 'expired' ? '0' : (usage?.plan_limit || 0);
    return null;
  };

  const getStatPercent = (key) => {
    if (key === 'clients') return usage?.client_usage_percent || 0;
    if (key === 'plans') return usage?.plan_usage_percent || 0;
    return 0;
  };

  const getStatRemaining = (key) => {
    if (key === 'clients') return usage?.clients_remaining || 0;
    if (key === 'plans') return usage?.plans_remaining || 0;
    return null;
  };

  const getPlanTierDisplay = () => {
    if (usage?.subscription_status === 'trial') return 'Free Trial';
    if (usage?.subscription_status === 'expired' && subscriptionStatus?.in_grace_period) return usage?.plan_tier || 'Expired';
    if (usage?.subscription_status === 'expired') return 'No Plan';
    return usage?.plan_tier || 'Active';
  };

  const getPlanTierSubtext = () => {
    if (usage?.subscription_status === 'trial' && usage?.trial_end) {
      const days = Math.ceil((new Date(usage.trial_end) - new Date()) / (1000 * 60 * 60 * 24));
      return `${days} days left (ends ${new Date(usage.trial_end).toLocaleDateString('en-IN', {day: 'numeric', month: 'short'})})`;
    }
    if (usage?.subscription_status === 'expired' && subscriptionStatus?.in_grace_period && usage?.subscription_end) {
      return `Expired ${new Date(usage.subscription_end).toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'})} - ${subscriptionStatus.grace_days_left} days grace left`;
    }
    if (usage?.subscription_status === 'expired') {
      return `Expired${usage?.trial_end ? ` on ${new Date(usage.trial_end).toLocaleDateString('en-IN', {day: 'numeric', month: 'short'})}` : ''}`;
    }
    if (usage?.subscription_end) return `Renews ${new Date(usage.subscription_end).toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'})}`;
    return 'Active';
  };

  const getExpiredText = (key) => {
    return key === 'clients' ? 'Expired - Upgrade to add clients' : 'Expired - Upgrade to create plans';
  };

  return (
    <div data-testid="advisor-dashboard" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />

      {/* Background accents - desktop only */}
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div
            className="absolute w-[500px] h-[500px] rounded-full bg-gradient-to-r from-blue-600/[0.07] to-cyan-500/[0.07] blur-3xl"
            animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            style={{ top: '-5%', right: '5%' }}
          />
          <motion.div
            className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-purple-600/[0.05] to-pink-500/[0.05] blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, 40, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            style={{ bottom: '10%', left: '0%' }}
          />
        </div>
      )}

      <motion.div
        className="max-w-7xl mx-auto relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Welcome Header */}
        <motion.div
          data-testid="dashboard-welcome"
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="flex items-center gap-3 mb-1">
            <motion.div
              className="w-2 h-8 rounded-full bg-gradient-to-b from-blue-500 to-cyan-400"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
              Welcome back, <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">{user?.name}</span>
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm sm:text-base">
            Here's your practice overview for this month
          </p>
        </motion.div>

        {/* Subscription Banner */}
        <motion.div
          data-testid="subscription-banner-wrapper"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <SubscriptionBanner usage={usage} />
        </motion.div>

        {/* Stats Cards */}
        <div data-testid="stats-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {STAT_CONFIGS.map((stat, idx) => {
            const Icon = stat.icon;
            const delay = isMobile ? 0 : 0.1 + idx * 0.07;
            const value = getStatValue(stat.key);
            const limit = getStatLimit(stat.key);
            const percent = getStatPercent(stat.key);
            const remaining = getStatRemaining(stat.key);

            return (
              <motion.div
                key={stat.key}
                data-testid={`stat-card-${stat.key}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay, ease: "easeOut" }}
                whileHover={!isMobile ? { y: -6, transition: { duration: 0.25, ease: "easeOut" } } : {}}
                className="group"
              >
                <Card className="p-5 h-full relative overflow-hidden border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 hover:shadow-xl hover:shadow-black/20">
                  {/* Glow effect on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500`} />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-medium tracking-wide uppercase text-gray-500">{stat.label}</h3>
                      <motion.div
                        className={`w-9 h-9 rounded-lg ${stat.iconBg} flex items-center justify-center`}
                        initial={{ scale: 0, rotate: -20 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: delay + 0.15, type: "spring", stiffness: 260, damping: 20 }}
                      >
                        <Icon className={`w-4 h-4 ${stat.iconColor}`} />
                      </motion.div>
                    </div>

                    {stat.key === 'plan_tier' ? (
                      <>
                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 capitalize">{getPlanTierDisplay()}</p>
                        <p className={`text-xs mt-2 ${usage?.subscription_status === 'expired' ? 'text-red-500 dark:text-red-400' : 'text-gray-500'}`}>
                          {getPlanTierSubtext()}
                        </p>
                      </>
                    ) : stat.key === 'total' ? (
                      <>
                        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                          <AnimatedNumber value={value} />
                        </p>
                        <p className="text-xs text-gray-500 mt-2">All-time</p>
                      </>
                    ) : (
                      <>
                        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                          <AnimatedNumber value={value} />
                          <span className="text-base font-normal text-gray-600 ml-1">/ {limit}</span>
                        </p>
                        {usage?.subscription_status !== 'expired' ? (
                          <>
                            <AnimatedProgress
                              value={percent}
                              gradient={percent >= 90 ? 'bg-gradient-to-r from-red-500 to-red-400' : percent >= 70 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' : stat.progressGradient}
                              delay={delay + 0.2}
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                              {remaining} remaining
                              {stat.key === 'plans' && usage?.billing_cycle === 'yearly' && usage?.next_plan_reset_date && (
                                <span className="ml-2 text-blue-400">
                                  Resets {new Date(usage.next_plan_reset_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-red-500 dark:text-red-400 mt-3">{getExpiredText(stat.key)}</p>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <motion.div
          data-testid="quick-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: isMobile ? 0 : 0.35 }}
        >
          <Card className="p-6 mb-8 border-gray-200 dark:border-gray-800/60">
            <div className="flex items-center gap-2 mb-5">
              <Target className="w-5 h-5 text-orange-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div whileHover={!isMobile ? { scale: 1.015 } : {}} whileTap={{ scale: 0.98 }}>
                <Button
                  data-testid="create-plan-btn"
                  onClick={() => {
                    if (usage?.subscription_status === 'expired') navigate('/advisor/pricing');
                    else navigate('/advisor/create-plan');
                  }}
                  disabled={!subscriptionStatus?.can_create_plans}
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 h-14 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed w-full shadow-lg shadow-blue-600/20 hover:shadow-blue-500/30 transition-shadow"
                  title={!subscriptionStatus?.can_create_plans ? (
                    subscriptionStatus?.is_blocked ? 'Account suspended - Renew to continue'
                      : subscriptionStatus?.in_grace_period ? 'Subscription expired - Renew to create new plans'
                      : 'Trial expired - Choose a plan to continue'
                  ) : ''}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {!subscriptionStatus?.can_create_plans ? (
                    subscriptionStatus?.is_blocked ? 'Account Suspended - Renew Now'
                      : subscriptionStatus?.in_grace_period ? 'Expired - Renew to Continue'
                      : 'Free Trial Expired - Choose Plan'
                  ) : 'Create New Plan'}
                </Button>
              </motion.div>
              <motion.div whileHover={!isMobile ? { scale: 1.015 } : {}} whileTap={{ scale: 0.98 }}>
                <Button
                  data-testid="view-clients-btn"
                  onClick={() => navigate('/advisor/clients')}
                  variant="outline"
                  className="h-14 text-base font-medium w-full border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View All Clients
                </Button>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* Recent Plans */}
        <motion.div
          data-testid="recent-plans"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: isMobile ? 0 : 0.4 }}
        >
          <Card className="p-6 mb-8 border-gray-200 dark:border-gray-800/60">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Plans</h2>
              </div>
              {recentPlans.length > 0 && (
                <Button
                  data-testid="view-all-plans-btn"
                  variant="ghost"
                  size="sm"
                  className="text-blue-400 hover:text-blue-300 text-xs"
                  onClick={() => navigate('/advisor/plans')}
                >
                  View all <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
            {recentPlans.length === 0 ? (
              <motion.div
                data-testid="empty-plans-state"
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <motion.div
                  className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center"
                  animate={!isMobile ? { y: [0, -6, 0] } : {}}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Sparkles className="w-7 h-7 text-blue-400/60" />
                </motion.div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">No plans created yet</p>
                <p className="text-gray-600 text-xs">Create your first financial plan to get started</p>
              </motion.div>
            ) : (
              <div className="space-y-2">
                {recentPlans.map((plan, index) => (
                  <motion.div
                    key={plan.plan_id || index}
                    data-testid={`plan-row-${index}`}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: isMobile ? 0 : 0.45 + index * 0.05 }}
                    whileHover={!isMobile ? { x: 4, backgroundColor: 'rgba(59, 130, 246, 0.04)' } : {}}
                    className="flex items-center justify-between p-3.5 rounded-lg cursor-pointer transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/30"
                    onClick={() => navigate(`/advisor/plan/${plan.plan_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                        <span className="text-blue-400 font-semibold text-sm">
                          {plan.client_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">{plan.client_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(plan.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-gray-600 dark:text-gray-400 transition-colors" />
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        </motion.div>

        {/* Tips Section */}
        <motion.div
          data-testid="tips-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: isMobile ? 0 : 0.45 }}
        >
          <Card className="p-6 border-gray-200 dark:border-gray-800/60">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Tips for Success</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', title: 'Create comprehensive plans', desc: 'Include all sections for maximum client value' },
                { icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-500/10', title: 'Review plans regularly', desc: 'Update client plans every 6 months' },
              ].map((tip, i) => (
                <motion.div
                  key={i}
                  data-testid={`tip-card-${i}`}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/30 transition-colors cursor-default"
                  whileHover={!isMobile ? { x: 4 } : {}}
                  transition={{ duration: 0.2 }}
                >
                  <div className={`w-9 h-9 rounded-lg ${tip.bg} flex items-center justify-center flex-shrink-0`}>
                    <tip.icon className={`w-4 h-4 ${tip.color}`} />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-0.5">{tip.title}</h3>
                    <p className="text-xs text-gray-500">{tip.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>

      </motion.div>
      <Footer />
    </div>
  );
}
